import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	CreditVendorOptions,
	MatterAdjustmentSalePrice,
	MatterPropertyType,
	NetOutHstOptions,
	PropertyStatus,
} from '@models/matter';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { openClose } from '@core/animations/animations';
import { cloneDeep } from 'lodash';

const VendorOptions: { [id in CreditVendorOptions]: string } = {
	net: 'Net sale price',
	agreed: 'Agreed sale price',
	total: 'Total sale price',
};

interface VendorOption {
	key: CreditVendorOptions;
	text: string;
}

@UntilDestroy()
@Component({
	selector: 'app-sale-price-dialog',
	templateUrl: 'sale-price-dialog.component.html',
	styleUrls: ['sale-price-dialog.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class SalePriceDialogComponent implements OnInit {
	adjustment: MatterAdjustmentSalePrice;
	readonly ICON = ICON;
	purchasePrice: number;
	originalPurchasePrice: number;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.purchasePrice) purchasePrice$: Observable<number>;
	@Select(AdjustmentsSelector.salePriceAdjustment)
	salePriceAdjustment$: Observable<MatterAdjustmentSalePrice>;

	@ViewSelectSnapshot(SelectedMatter.propertyType)
	propertyType: MatterPropertyType;
	public vendorOptions: VendorOption[] = Object.entries(VendorOptions).map(
		([key, value]) => ({
			key: key as CreditVendorOptions,
			text: value,
		}),
	);

	public propertyStatuses = PropertyStatus;

	public netOutHstOptions = NetOutHstOptions;

	@ViewChild('SalePriceBreakdown', { static: false }) breakdownComponent;

	includeHstLabel = false;
	showInfoMessage = false;

	public backString = 'Adjustments';
	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: { matterId: string; backString?: string },
		private dialogRef: MatDialogRef<SalePriceDialogComponent>,
		private matterDispatch: MatterDispatch,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		if (this.data.backString) {
			this.backString = this.data.backString;
		}
		this.purchasePrice$
			.pipe(untilDestroyed(this))
			.subscribe((_purchasePrice) => {
				this.originalPurchasePrice = _purchasePrice;
				this.purchasePrice = _purchasePrice;
				this.changed();
			});

		this.salePriceAdjustment$
			.pipe(untilDestroyed(this))
			.subscribe((_adjustment) => {
				this.adjustment = cloneDeep(_adjustment);
				this.netOutChanged();
				this.changed();
				this.toggleIncludeHstLabel();
				this.toggleInfoMessage();
			});
	}

	toggleIncludeHstLabel() {
		this.includeHstLabel =
			this.adjustment.netOutHst === NetOutHstOptions.YES_HST ||
			this.adjustment.netOutHst === NetOutHstOptions.YES_HST_NO_REBATE ||
			this.adjustment.netOutHst === NetOutHstOptions.YES_NO_HST;
	}

	toggleInfoMessage() {
		this.showInfoMessage = this.adjustment
			? (this.adjustment.netOutHst === NetOutHstOptions.YES_HST &&
					this.adjustment.creditVendor === CreditVendorOptions.NET) ||
				this.adjustment.netOutHst === NetOutHstOptions.YES_HST_NO_REBATE ||
				(this.adjustment.netOutHst === NetOutHstOptions.YES_NO_HST &&
					this.adjustment.creditVendor === CreditVendorOptions.NET)
			: false;
	}

	onNetOutChange() {
		if (
			this.adjustment.netOutHst === NetOutHstOptions.YES_HST ||
			this.adjustment.netOutHst === NetOutHstOptions.YES_NO_HST
		) {
			this.adjustment.creditVendor = CreditVendorOptions.NET;
		}
		this.toggleIncludeHstLabel();
		this.toggleInfoMessage();
		this.netOutChanged();
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	netOutChanged() {
		const vendorOptionsContainsNet = this.vendorOptions.find(
			(x) => x.key === CreditVendorOptions.NET,
		);
		if (
			this.adjustment.netOutHst === NetOutHstOptions.NO &&
			vendorOptionsContainsNet
		) {
			this.vendorOptions = [
				{ key: CreditVendorOptions.AGREED, text: 'Agreed sale price' },
				{ key: CreditVendorOptions.TOTAL, text: 'Total sale price' },
			];
			if (this.adjustment.creditVendor === CreditVendorOptions.NET) {
				this.adjustment.creditVendor = CreditVendorOptions.TOTAL;
			}
		} else if (
			this.adjustment.netOutHst !== NetOutHstOptions.NO &&
			!vendorOptionsContainsNet
		) {
			this.vendorOptions = Object.entries(VendorOptions).map(
				([key, value]) => ({
					key: key as CreditVendorOptions,
					text: value,
				}),
			);
		}

		this.changed();
	}

	changed(): void {
		this.toggleInfoMessage();
		this.breakdownComponent?.calculateSaleValues();
	}

	saveModal(): void {
		this.adjustmentDispatch.Set(this.adjustment);
		if (this.purchasePrice !== this.originalPurchasePrice) {
			this.matterDispatch.SetSalePrice(this.matterId, this.purchasePrice);
		}
		this.dialogRef.close();
	}

	trackBycreditVendor(index: number, vendorOption: VendorOption) {
		return vendorOption.key;
	}
}
